import { platformSettingsInput } from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Field } from "@/components/app/field";
import { Page, PageHeader } from "@/components/app/page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAdminTRPC } from "@/lib/trpc";

/** Settings of the whole service: for now, how long invitations are valid. */
export function AdminSettingsPage() {
  const { t } = useTranslation();
  const trpc = useAdminTRPC();
  const { data: settings } = useQuery(trpc.settings.get.queryOptions());
  const update = useMutation(trpc.settings.update.mutationOptions());
  const [days, setDays] = useState("");
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (settings) setDays(String(settings.inviteDays));
  }, [settings]);

  return (
    <Page>
      <PageHeader title={t("admin.settings")} subtitle={t("admin.settingsBody")} />
      <Card className="max-w-xl p-4 sm:p-5">
        <form
          className="flex flex-col gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            const parsed = platformSettingsInput.safeParse({ inviteDays: Number(days) });
            if (!parsed.success || !/^\d+$/.test(days.trim())) {
              setError("validation.inviteDays");
              return;
            }
            setError(undefined);
            await update.mutateAsync(parsed.data);
            toast.success(t("admin.saved"));
          }}
        >
          <Field
            label={t("admin.inviteDays")}
            htmlFor="invite-days"
            error={error}
            hint={t("admin.inviteDaysHint")}
          >
            <div className="flex items-center gap-2">
              <Input
                id="invite-days"
                inputMode="numeric"
                className="w-24"
                value={days}
                aria-invalid={error ? true : undefined}
                onChange={(e) => setDays(e.target.value)}
              />
              <span className="text-sm text-muted-foreground">{t("admin.days")}</span>
            </div>
          </Field>
          <Button type="submit" className="self-start" disabled={update.isPending || !settings}>
            {t("common.save")}
          </Button>
        </form>
      </Card>
    </Page>
  );
}
